import React, { useEffect, useRef, useState, useMemo } from "react";
import { Company } from "@captiva/shared-types";
import {
  Divider,
  Loader,
  Notification,
  Text,
  Button,
  TextInput,
  Select,
  Group,
  Stack,
  Tabs,
  Paper,
  Grid,
  Badge,
} from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";
import Pay from "../components/shared/pay";
import BillingInfo from "../components/shared/billing-info";
import { PageHeader, ScrollableTabs } from "../components/shared";
import { db, storage, auth } from "../firebase";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  IconBuilding,
  IconCoin,
  IconReceipt,
  IconCopy,
  IconCheck,
  IconEdit,
  IconUpload,
  IconAlertCircle,
} from "@tabler/icons-react";

const TabItem = Tabs.Tab as unknown as React.ComponentType<{
  label: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  tabKey?: string;
  color?: string;
  [key: string]: any;
}>;

export function CompanyDetails({ companyId }: { companyId: string }) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [searchParams, setSearchParams] = useSearchParams();
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchedCompany, setFetchedCompany] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoUploadError, setLogoUploadError] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialTab = useMemo(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "credits") return 1;
    if (tabParam === "billing") return 2;
    return 0;
  }, [searchParams]);

  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "credits") setActiveTab(1);
    else if (tabParam === "billing") setActiveTab(2);
    else if (tabParam === "profile") setActiveTab(0);
  }, [searchParams]);

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    const tabName = index === 1 ? "credits" : index === 2 ? "billing" : "profile";
    setSearchParams({ tab: tabName }, { replace: true });
  };

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [addresLine1, setAddresLine1] = useState("");
  const [addresLine2, setAddresLine2] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [fuelUnitOfMeasure, setFuelUnitOfMeasure] = useState("LITRES");

  const baseUrl = process.env.REACT_APP_APP_ENV === "local"
    ? "http://127.0.0.1:5001/twodata-dev/europe-west3/tenantapi"
    : "https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi";

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!fetchedCompany && companyId && currentUser) {
      const fetchCompany = async () => {
        setFetchedCompany(true);
        setLoading(true);
        let companyData: any = null;

        try {
          const token = await currentUser.getIdToken();
          const response = await fetch(
            `${baseUrl}/companies/${companyId}?` + new URLSearchParams({ companyId }),
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": process.env.REACT_APP_X_API_KEY || "0f247291-b4bb-4895-91ac-ba26af7e55a2",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.ok) {
            companyData = await response.json();
          }
        } catch (error) {
          console.warn("API fetch failed, falling back to direct Firestore fetch:", error);
        }

        if (!companyData) {
          try {
            const compSnap = await getDoc(doc(db, "companies", companyId));
            if (compSnap.exists()) {
              companyData = { id: compSnap.id, ...compSnap.data() };
            }
          } catch (fsErr) {
            console.error("Firestore company fetch error:", fsErr);
          }
        }

        if (companyData) {
          setCompany(companyData);
          setName(companyData.name || "");
          setAddresLine1(companyData.addresLine1 || companyData.street || "");
          setAddresLine2(companyData.addresLine2 || "");
          setCity(companyData.city || "");
          setProvince(companyData.province || "");
          setCountry(companyData.country || "");
          setPostalCode(companyData.postalCode || "");
          setFuelUnitOfMeasure(companyData.fuelUnitOfMeasure || "LITRES");
          setLogoUrl(companyData.logoUrl || "");
        }
        setLoading(false);
      };
      fetchCompany();
    }
  }, [companyId, fetchedCompany, auth.currentUser, baseUrl]);

  async function handleSaveCompany() {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setLoading(true);
    const updatedData = {
      companyId,
      name,
      addresLine1,
      addresLine2,
      city,
      province,
      country,
      postalCode,
      fuelUnitOfMeasure,
    };

    let saved = false;
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${baseUrl}/companies/${companyId}?` + new URLSearchParams({ companyId }),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_X_API_KEY || "0f247291-b4bb-4895-91ac-ba26af7e55a2",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedData),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCompany(data);
        saved = true;
      }
    } catch (err) {
      console.warn("API PUT failed, updating Firestore directly:", err);
    }

    if (!saved) {
      try {
        await updateDoc(doc(db, "companies", companyId), updatedData);
        setCompany((prev: any) => ({ ...prev, ...updatedData }));
        saved = true;
      } catch (fsErr) {
        console.error("Failed to update company in Firestore:", fsErr);
      }
    }

    setLoading(false);
    if (saved) {
      setIsEditing(false);
      setMessage("Company details updated successfully.");
    } else {
      setErrorMessage("Failed to update company details. Please try again.");
    }
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 200 * 1024) {
      setLogoUploadError("File size exceeds 200kb limit.");
      return;
    }
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      setLogoUploadError("Only PNG and JPEG images are allowed.");
      return;
    }

    setLogoUploadError("");
    setLogoUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `companies/${companyId}/logo.${ext}`;
      const r = storageRef(storage, path);
      const snapshot = await uploadBytes(r, file);
      const url = await getDownloadURL(snapshot.ref);
      setLogoUrl(url);

      try {
        await updateDoc(doc(db, "companies", companyId), { logoUrl: url });
      } catch (e) {
        console.warn("Could not save logoUrl to firestore:", e);
      }
      setMessage("Company logo uploaded successfully.");
    } catch (err) {
      console.error("Error uploading logo:", err);
      setLogoUploadError("Upload failed. Please try again.");
    } finally {
      setLogoUploading(false);
    }
  }

  const handleCopyActivationCode = () => {
    const code = company?.activationCode || company?.companyId || companyId;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <PageHeader
        title="Company Settings"
        subtitle="Manage your company profile, fueling units, credits, and invoicing details"
        alert={
          message || errorMessage ? (
            <>
              {message && (
                <Notification
                  icon={<IconCheck size={16} />}
                  color="green"
                  title="Success"
                  onClose={() => setMessage("")}
                  mb="xs"
                >
                  {message}
                </Notification>
              )}
              {errorMessage && (
                <Notification
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  title="Error"
                  onClose={() => setErrorMessage("")}
                  mb="xs"
                >
                  {errorMessage}
                </Notification>
              )}
            </>
          ) : undefined
        }
      />

      {loading && !company ? (
        <Loader style={{ margin: "2em auto", display: "block" }} />
      ) : (
        <ScrollableTabs active={activeTab} onTabChange={handleTabChange} color="cap-blue" style={{ width: "100%" }}>
          <TabItem color="cap-blue" label="Company Profile" icon={<IconBuilding size={16} />} tabKey="profile">
            <Stack spacing="md" mt="md">
              {/* Activation Code Banner */}
              <Paper
                p="md"
                radius="md"
                withBorder
                style={{ border: "1px solid #e9ecef", backgroundColor: "#f8f9fa" }}
              >
                <Group position="apart" align="center" spacing="md">
                  <div>
                    <Text
                      size="xs"
                      weight={700}
                      color="dimmed"
                      style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                    >
                      Company Activation Code
                    </Text>
                    <Group spacing="xs" align="center" mt={4}>
                      <Text
                        size="xl"
                        weight={700}
                        style={{ color: "#1A1B1E", letterSpacing: "0.02em" }}
                      >
                        {company?.activationCode || company?.companyId || companyId}
                      </Text>
                    </Group>
                    <Text size="xs" color="dimmed" mt={2}>
                      Share this activation code with operators and managers to link new accounts to your company.
                    </Text>
                  </div>
                  <Button
                    variant="outline"
                    size={isMobile ? "md" : "sm"}
                    color={copied ? "green" : "cap-blue"}
                    leftIcon={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    onClick={handleCopyActivationCode}
                    style={{ minHeight: isMobile ? 44 : 36 }}
                  >
                    {copied ? "Copied!" : "Copy Code"}
                  </Button>
                </Group>
              </Paper>

              {/* Company Details Card */}
              <Paper p="lg" radius="md" withBorder style={{ border: "1px solid #e9ecef" }}>
                {!isEditing ? (
                  <>
                    <Group position="apart" mb="md" align="flex-start">
                      <div>
                        <Text size="md" weight={600} color="dark">
                          Organization Details
                        </Text>
                        <Text size="xs" color="dimmed" mt={2}>
                          Primary business name, headquarters location, and mobile measurement preferences
                        </Text>
                      </div>
                      <Button
                        size={isMobile ? "md" : "sm"}
                        variant="outline"
                        color="cap-blue"
                        leftIcon={<IconEdit size={16} />}
                        onClick={() => setIsEditing(true)}
                        style={{ minHeight: isMobile ? 44 : 36 }}
                      >
                        Edit Profile
                      </Button>
                    </Group>

                    <Divider my="sm" />

                    <Grid gutter="xl" mt="xs">
                      {/* Section 1: Name & Measurement */}
                      <Grid.Col span={12} md={4}>
                        <Stack spacing="md">
                          <div>
                            <Text size="xs" weight={600} color="dimmed" mb={2}>
                              Company Name
                            </Text>
                            <Text size="sm" weight={700} color="dark">
                              {company?.name || "Untitled Organization"}
                            </Text>
                          </div>
                          <div>
                            <Text size="xs" weight={600} color="dimmed" mb={4}>
                              Fuel Unit of Measure
                            </Text>
                            <Badge size="sm" color="cap-blue" variant="light">
                              {company?.fuelUnitOfMeasure || "LITRES"}
                            </Badge>
                          </div>
                        </Stack>
                      </Grid.Col>

                      {/* Section 2: Operating Address */}
                      <Grid.Col span={12} md={4}>
                        <Stack spacing="xs">
                          <Text size="xs" weight={600} color="dimmed" mb={2}>
                            Operating Address
                          </Text>
                          <Text size="sm" weight={500} color="dark">
                            {company?.addresLine1 || company?.street || "—"}
                          </Text>
                          {company?.addresLine2 && (
                            <Text size="sm" color="dark">
                              {company.addresLine2}
                            </Text>
                          )}
                          <Text size="sm" color="dark">
                            {[company?.city, company?.province, company?.postalCode].filter(Boolean).join(", ") || "—"}
                          </Text>
                          {company?.country && (
                            <Text size="sm" color="dimmed">
                              {company.country}
                            </Text>
                          )}
                        </Stack>
                      </Grid.Col>

                      {/* Section 3: Company Logo */}
                      <Grid.Col span={12} md={4}>
                        <Stack spacing="xs">
                          <Text size="xs" weight={600} color="dimmed">
                            Company Logo
                          </Text>
                          {logoUrl || company?.logoUrl ? (
                            <div
                              style={{
                                border: "1px solid #eaeaea",
                                borderRadius: 8,
                                padding: 12,
                                background: "#fff",
                                width: "100%",
                                maxWidth: 180,
                                height: 110,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={logoUrl || company?.logoUrl}
                                alt="Company Logo"
                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                              />
                            </div>
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                maxWidth: 180,
                                height: 110,
                                border: "1px dashed #ccc",
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#fcfcfc",
                              }}
                            >
                              <Text size="xs" color="dimmed">
                                No logo uploaded
                              </Text>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            onChange={handleLogoUpload}
                            style={{ display: "none" }}
                          />
                          <Button
                            variant="outline"
                            size={isMobile ? "md" : "sm"}
                            color="cap-blue"
                            leftIcon={<IconUpload size={16} />}
                            onClick={() => fileInputRef.current?.click()}
                            loading={logoUploading}
                            style={{ width: "100%", maxWidth: isMobile ? "100%" : 180, minHeight: isMobile ? 44 : 36 }}
                            mt={4}
                          >
                            Upload Logo
                          </Button>
                          <Text size="xs" color="dimmed" style={{ fontSize: "11px" }}>
                            PNG or JPEG, max 200KB.
                          </Text>
                          {logoUploadError && (
                            <Text color="red" size="xs">
                              {logoUploadError}
                            </Text>
                          )}
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </>
                ) : (
                  <Stack spacing="sm">
                    <Text size="md" weight={600} color="dark">
                      Edit Organization Details
                    </Text>
                    <Divider my="xs" />
                    <Grid gutter="sm">
                      <Grid.Col span={8}>
                        <TextInput
                          label="Company Name"
                          required
                          size="sm"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Select
                          label="Fuel Unit of Measure"
                          required
                          size="sm"
                          data={["LITRES", "GALLONS"]}
                          value={fuelUnitOfMeasure}
                          onChange={(val) => setFuelUnitOfMeasure(val || "LITRES")}
                        />
                      </Grid.Col>
                    </Grid>

                    <TextInput
                      label="Address Line 1"
                      required
                      size="sm"
                      value={addresLine1}
                      onChange={(e) => setAddresLine1(e.target.value)}
                    />
                    <TextInput
                      label="Address Line 2 (Optional)"
                      size="sm"
                      value={addresLine2}
                      onChange={(e) => setAddresLine2(e.target.value)}
                    />

                    <Grid gutter="sm">
                      <Grid.Col span={6}>
                        <TextInput
                          label="City"
                          required
                          size="sm"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <TextInput
                          label="State / Province"
                          required
                          size="sm"
                          value={province}
                          onChange={(e) => setProvince(e.target.value)}
                        />
                      </Grid.Col>
                    </Grid>

                    <Grid gutter="sm">
                      <Grid.Col span={6}>
                        <TextInput
                          label="Country"
                          required
                          size="sm"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <TextInput
                          label="Postal Code"
                          required
                          size="sm"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                        />
                      </Grid.Col>
                    </Grid>

                    <Divider my="sm" />

                    <Group position="right" spacing="sm">
                      <Button variant="default" color="gray" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : 36 }} onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                      <Button size={isMobile ? "md" : "sm"} color="cap-navy" style={{ minHeight: isMobile ? 44 : 36 }} onClick={handleSaveCompany} loading={loading}>
                        Save Changes
                      </Button>
                    </Group>
                  </Stack>
                )}
              </Paper>
            </Stack>
          </TabItem>

          <TabItem color="cap-blue" label="Credits & Usage" icon={<IconCoin size={16} />} tabKey="credits">
            <div style={{ marginTop: "1rem" }}>
              <Pay companyId={companyId} />
            </div>
          </TabItem>

          <TabItem color="cap-blue" label="Billing Details" icon={<IconReceipt size={16} />} tabKey="billing">
            <div style={{ marginTop: "1rem" }}>
              <BillingInfo companyId={companyId} />
            </div>
          </TabItem>
        </ScrollableTabs>
      )}
    </>
  );
}

export default CompanyDetails;
